import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { useHistory, useParams } from "react-router-dom";
import { useTimezone } from "../../hooks/useTimezone";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import { blue, green, orange, red } from "@material-ui/core/colors";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

import { Tooltip, Menu, MenuItem, ListItemIcon, Chip, useMediaQuery } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { useTicketPopup } from "../../context/TicketPopup/TicketPopupContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import api, { getProfilePicUrl } from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";

import AndroidIcon from "@material-ui/icons/Android";
import EmojiEmotionsIcon from "@material-ui/icons/EmojiEmotions";
import SettingsIcon from "@material-ui/icons/Settings";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import PeopleIcon from "@material-ui/icons/People";
import ReplayIcon from "@material-ui/icons/Replay";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import TelegramIcon from "@material-ui/icons/Telegram";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ChatIcon from "@material-ui/icons/Chat";
import LockIcon from "@material-ui/icons/Lock";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import AppointmentModal from "../AppointmentModal";
import ConfirmationModal from "../ConfirmationModal";
import { toast } from "react-toastify";
import TicketMessagesDialog from "../TicketMessagesDialog";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import { getInitials } from "../../helpers/getInitials";
import { getContactCountryFlagInfo } from "../../helpers/phoneCountryFlag";
import { generateColor } from "../../helpers/colorGenerator";
import useSettings, { isSettingTruthy } from "../../hooks/useSettings";

const useStyles = makeStyles((theme) => ({
  // ── Rediseño 2026-09: tarjeta elevada, densidad ajustada ──
  ticket: {
    position: "relative",
    padding: theme.spacing(1, 1.25, 1, 1.75),
    marginBottom: theme.spacing(0.75),
    borderRadius: "14px",
    transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, background-color 140ms ease",
    backgroundColor: theme.palette.background.paper,
    border: "none",
    boxShadow: "none",
    minHeight: "auto",
    overflow: "visible",
    "& .MuiListItemText-root": {
      minWidth: 0,
      overflow: "hidden",
      paddingRight: 96,
      marginTop: 0,
      marginBottom: 0,
    },
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? "rgba(255,255,255,0.04)" : "#f8f9fc",
      borderColor: "transparent",
      boxShadow: "none",
      transform: "none",
    },
    "&.Mui-selected": {
      backgroundColor: theme.mode === 'dark'
        ? "rgba(129,140,248,0.16)"
        : "#eef0ff",
      borderColor: "transparent",
      boxShadow: "none",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75, 1, 0.75, 1.25),
      marginBottom: 0,
      borderRadius: 0,
      "& .MuiListItemText-root": {
        paddingRight: 78,
      },
    },
  },
  mobileActionButton: {
    width: 28,
    height: 28,
    minWidth: "28px !important",
    minHeight: "28px !important",
    padding: "0 !important",
    borderRadius: 8,
    border: "1px solid",
  },

  // Fila con mensajes sin leer: lavado de color de marca + realce del cabo
  unreadTicket: {
    background: theme.mode === 'dark'
      ? "linear-gradient(90deg, rgba(96,165,250,0.13), transparent 62%)"
      : "linear-gradient(90deg, rgba(29,78,216,0.07), transparent 62%)",
    "& $ticketQueueColor": {
      backgroundColor: `${theme.palette.primary.main} !important`,
      width: "5px",
    },
  },

  pendingTicket: {
    cursor: "pointer",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 4,
    marginBottom: 2,
    padding: "4px 8px",
    fontWeight: 600,
    borderRadius: 6,
    fontSize: "0.75em",
    whiteSpace: "nowrap",
    display: "inline-block",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    position: "absolute",
    alignSelf: "center",
    marginRight: 8,
    marginLeft: "auto",
    top: "-4px", // ✅ AJUSTADO: De 4px a -4px - Más abajo, cerca del borde superior izquierdo del avatar
    left: "-4px", // ✅ AJUSTADO: De 8px a -4px - Más cerca del borde izquierdo del avatar
    borderRadius: 0,
    zIndex: 10,
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: theme.palette.primary.main,
    color: "#FFF",
    marginRight: 4,
    marginBottom: 0,
    padding: "2px 8px",
    fontWeight: 600,
    borderRadius: 999,
    fontSize: "10.5px",
    lineHeight: "16px",
    whiteSpace: "nowrap",
    display: "inline-block",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    marginLeft: "3px",
    marginBottom: "2px",
  },

  lastMessageTime: {
    textAlign: "right",
    position: "relative",
    marginBottom: "4px",
    padding: 0,
    fontSize: "11px",
    fontWeight: 500,
    zIndex: 20,
    letterSpacing: "0.01em",
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.secondary,
    textTransform: "none",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    lineHeight: "1.2",
    display: "inline-block",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  lastMessageTimeUnread: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: 4,
    marginLeft: 0,
    marginTop: 2,
    fontSize: "13px",
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
    display: "block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },


  badgeStyle: {
    color: theme.palette.primary.contrastText || "#fff",
    backgroundColor: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "11px",
    minWidth: "20px",
    height: "20px",
    borderRadius: "999px",
    fontVariantNumeric: "tabular-nums",
    boxShadow: `0 2px 6px -1px ${theme.palette.primary.main}55`,
    border: `2px solid ${theme.palette.background.paper}`,
  },
  unreadCountPill: {
    minWidth: 22,
    height: 22,
    padding: "0 6px",
    borderRadius: 999,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: "22px",
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    boxShadow: `0 2px 6px -1px ${theme.palette.primary.main}66`,
    zIndex: 21,
  },
  unreadCountPillMobile: {
    flex: "none",
    minWidth: 18,
    height: 18,
    lineHeight: "18px",
    fontSize: 10,
    padding: "0 5px",
    boxShadow: "none",
  },

  ticketQueueColor: {
    flex: "none",
    width: "4px",
    position: "absolute",
    top: "10px",
    bottom: "10px",
    left: "6px",
    borderRadius: "999px",
    transition: "width 140ms ease, background-color 140ms ease",
  },

  ticketInfo: {
    position: "relative",
    top: -13
  },
  secondaryContentSecond: {
    display: 'flex',
    alignItems: "center",
    flexWrap: "nowrap",
    flexDirection: "row",
    gap: 6,
    marginBottom: 6,
    marginTop: 2,
    lineHeight: 0,
  },

  // ✅ ESTILOS PARA BOTONES CON ICONOS
  actionIconButton: {
    minWidth: 26,
    width: 26,
    height: 26,
    padding: "0 !important",
    border: "none",
    backgroundColor: "transparent",
    lineHeight: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  
  // ✅ ESTILO PARA ICONO DE WHATSAPP
  whatsappIconButton: {
    minWidth: "28px",
    height: "28px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    cursor: "pointer",
    padding: "4px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    },
  },

  acceptButton: {
    color: "#4caf50",
    borderColor: "#4caf50",
    backgroundColor: "rgba(76, 175, 80, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.15)",
      borderColor: "#2e7d32",
      color: "#2e7d32",
    },
  },

  closeButton: {
    color: "#f44336",
    borderColor: "#f44336",
    backgroundColor: "rgba(244, 67, 54, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(244, 67, 54, 0.15)",
      borderColor: "#c62828",
      color: "#c62828",
    },
  },

  transferButton: {
    color: "#2196f3",
    borderColor: "#2196f3",
    backgroundColor: "rgba(33, 150, 243, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(33, 150, 243, 0.15)",
      borderColor: "#1565c0",
      color: "#1565c0",
    },
  },

  reopenButton: {
    color: "#ff9800",
    borderColor: "#ff9800",
    backgroundColor: "rgba(255, 152, 0, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(255, 152, 0, 0.15)",
      borderColor: "#ef6c00",
      color: "#ef6c00",
    },
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  "@keyframes ticketPresencePulse": {
    "0%, 100%": { opacity: 0.35 },
    "50%": { opacity: 1 },
  },
  presence: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontStyle: "italic",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    "&::before": {
      content: '""',
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: "currentColor",
      animation: "$ticketPresencePulse 1.3s ease-in-out infinite",
    },
    "@media (prefers-reduced-motion: reduce)": {
      "&::before": { animation: "none", opacity: 0.7 },
    },
  },
  ticketNumber: {
    fontSize: "11px",
    fontWeight: "700",
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginTop: "4px",
    lineHeight: "1.2",
    width: "100%",
    letterSpacing: "0.5px",
    opacity: 0.8,
  },

  avatarActive: {
    width: "46px",
    height: "46px",
    borderRadius: "14px",
    fontWeight: 600,
    boxShadow: theme.mode === 'dark'
      ? "0 1px 2px rgba(0,0,0,0.4)"
      : "0 1px 3px rgba(20,28,48,0.14)",
    border: `2px solid ${theme.palette.background.paper}`,
    [theme.breakpoints.down("sm")]: {
      width: 42,
      height: 42,
      borderRadius: "50%",
    },
  },

  avatarPending: {
    width: "44px",
    height: "44px",
    borderRadius: "13px",
    fontWeight: 600,
    boxShadow: theme.mode === 'dark'
      ? "0 1px 2px rgba(0,0,0,0.4)"
      : "0 1px 3px rgba(20,28,48,0.12)",
    border: `2px solid ${theme.palette.background.paper}`,
    [theme.breakpoints.down("sm")]: {
      width: 42,
      height: 42,
      borderRadius: "50%",
    },
  },

  avatarContainer: {
    position: "relative",
    display: "inline-block",
  },

  agentAvatarBadge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      width: 20,
      height: 20,
      bottom: -1,
      right: -1,
    },
  },

  agentAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
  },

  /** M3: nombre asignado (si hay) + #ticket bajo el avatar; mismo # que en TicketInfo cabecera */
  avatarMetaBelow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: theme.spacing(0.5),
    maxWidth: "76px",
    textAlign: "center",
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  avatarMetaAssignee: {
    fontSize: "10px",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  avatarMetaTicketId: {
    fontSize: "10px",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginTop: "1px",
    letterSpacing: "0.02em",
  },
}));

const TicketListItemCustom = ({ ticket, status }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { getTimeAgo } = useTimezone(); // ✅ NUEVO: Para acceder al theme en el componente
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [tag, setTag] = useState([]);
  const [ticketUser, setTicketUser] = useState(null);
  const [lastInteractionLabel, setLastInteractionLabel] = useState('');
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { openPopup } = useTicketPopup() || {};
  const { user } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { profile } = user || {};
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [closeTicketConfirmOpen, setCloseTicketConfirmOpen] = useState(false);
  const [removeTagsOnCloseEnabled, setRemoveTagsOnCloseEnabled] = useState(false);
  const [sendTicketCloseMessageEnabled, setSendTicketCloseMessageEnabled] =
    useState(false);
  const pendingClosePayloadRef = useRef(null);
  /** Reduce ráfaga HTTP: primero foto contacto, luego foto agente (0296 Fase C). */
  const [agentPortraitDeferred, setAgentPortraitDeferred] = useState(true);
  const { getAll } = useSettings();
  const presenceMessage = { composing: "Digitando...", recording: "Gravando..." };

  // ✅ Helper para evitar setState después de unmount (fix warning React)
  const safeSetLoading = (value) => {
    if (isMounted.current) setLoading(value);
  };
  
  // ✅ Obtener estado de WhatsApp desde el contexto (más actualizado)
  const ticketWhatsApp = ticket?.whatsappId
    ? (whatsApps || []).find(w => w.id === ticket.whatsappId)
    : null;
  const whatsappStatus = ticketWhatsApp?.status || ticket?.whatsapp?.status || "DISCONNECTED";
  const isWhatsAppConnected = whatsappStatus === "CONNECTED";
  const isTelegramTicket =
    ticket?.channel === "telegram" ||
    ticketWhatsApp?.type === "telegram" ||
    ticket?.whatsapp?.type === "telegram";
  const ChannelIcon = isTelegramTicket ? TelegramIcon : WhatsAppIcon;
  const channelColor = isTelegramTicket ? "#229ED9" : "#22c55e";

  const countryFlagInfo = useMemo(
    () => getContactCountryFlagInfo(ticket?.contact, ticket, i18n.language),
    [ticket?.contact, ticket?.contact?.number, ticket?.contact?.isGroup, ticket?.isGroup, i18n.language]
  );

  // ✅ Funciones para manejar el menú desplegable
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const menuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getAll();
        const rowTags = list.find((s) => s.key === "removeTagsOnCloseTicket");
        const rowMsg = list.find((s) => s.key === "sendTicketCloseMessage");
        if (!cancelled) {
          setRemoveTagsOnCloseEnabled(
            rowTags ? isSettingTruthy(rowTags.value) : false
          );
          setSendTicketCloseMessageEnabled(
            rowMsg ? isSettingTruthy(rowMsg.value) : false
          );
        }
      } catch (_e) {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAll]);

  const handleContactPortraitLoaded = useCallback(() => {
    setAgentPortraitDeferred(false);
  }, []);

  useEffect(() => {
    setAgentPortraitDeferred(true);
    const t = setTimeout(() => {
      if (isMounted.current) setAgentPortraitDeferred(false);
    }, 380);
    return () => clearTimeout(t);
  }, [ticket?.id, ticket?.contact?.profilePicUrl]);

  const contactPortraitImgProps = useMemo(
    () => ({
      loading: "lazy",
      decoding: "async",
      // DOM / React: el atributo HTML es `fetchpriority` (minúsculas); evita warning en consola.
      fetchpriority: "low",
      onLoad: handleContactPortraitLoaded,
    }),
    [handleContactPortraitLoaded]
  );

  const agentPortraitImgProps = useMemo(
    () => ({
      loading: "lazy",
      decoding: "async",
      fetchpriority: "low",
    }),
    []
  );

  const executeCloseTicketPutWithToast = async (payload, successMsg) => {
    if (!ticket?.id) return;
    safeSetLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, payload);
      toast.success(successMsg);
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

  const needsCloseConfirmModal =
    removeTagsOnCloseEnabled || sendTicketCloseMessageEnabled;

  const getCloseConfirmModalBody = () => {
    const tags = removeTagsOnCloseEnabled;
    const snd = sendTicketCloseMessageEnabled;
    if (tags && snd) {
      return i18n.t("ticketsList.buttons.confirmCloseBodyTagsAndMessage");
    }
    if (tags) {
      return i18n.t("ticketsList.buttons.confirmCloseWithTagsMessage");
    }
    if (snd) {
      return i18n.t("ticketsList.buttons.confirmCloseWithSendMessage");
    }
    return "";
  };

  const requestCloseTicket = (payload, successMsg) => {
    if (needsCloseConfirmModal) {
      pendingClosePayloadRef.current = { payload, successMsg };
      setCloseTicketConfirmOpen(true);
    } else {
      executeCloseTicketPutWithToast(payload, successMsg);
    }
  };

  const handleConfirmCloseTicketModal = () => {
    setCloseTicketConfirmOpen(false);
    const pending = pendingClosePayloadRef.current;
    pendingClosePayloadRef.current = null;
    if (pending?.payload) {
      executeCloseTicketPutWithToast(
        pending.payload,
        pending.successMsg ||
          i18n.t("ticketsList.buttons.ticketClosed") ||
          "Ticket cerrado correctamente"
      );
    }
  };
  
  // ✅ Funciones para las acciones del menú
  const handleResolve = async () => {
    if (!ticket?.id) return;
    handleMenuClose();
    requestCloseTicket(
      { status: "closed", userId: user?.id },
      "Ticket resuelto correctamente"
    );
  };

  const handleResolveWithoutFarewell = async () => {
    if (!ticket?.id) return;
    handleMenuClose();
    requestCloseTicket(
      { status: "closed", userId: user?.id, sendFarewell: false },
      "Ticket resuelto sin despedida"
    );
  };

  const handleReturn = async () => {
    if (!ticket?.id) return;
    handleMenuClose();
    safeSetLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "pending",
        userId: null,
      });
      toast.success("Ticket devuelto correctamente");
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

  const handleSchedule = () => {
    if (!ticket?.id) return;
    handleMenuClose();
    setScheduleModalOpen(true);
  };

  const handleMarkAsUnread = async () => {
    if (!ticket?.id) return;
    handleMenuClose();
    safeSetLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        unreadMessages: 1,
      });
      toast.success("Ticket marcado como no leído");
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

  const handleKeepWithMe = async () => {
    if (!ticket?.id) return;
    handleMenuClose();
    safeSetLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        userId: user?.id,
      });
      toast.success("Ticket asignado a ti");
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

  const handleDelete = () => {
    if (!ticket?.id) return;
    handleMenuClose();
    setConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!ticket?.id) return;
    setConfirmationOpen(false);
    safeSetLoading(true);
    try {
      await api.delete(`/tickets/${ticket.id}`);
      toast.success("Ticket eliminado correctamente");
      const path = window.location.pathname || "";
      const openHere =
        (ticket.uuid && path.includes(`/tickets/${ticket.uuid}`)) ||
        (ticket.id && path.includes(`/tickets/${ticket.id}`));
      if (openHere) {
        history.push("/tickets");
      }
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

  // ✅ FUNCIÓN PARA DETERMINAR QUÉ BOTONES MOSTRAR SEGÚN EL ESTADO DEL GRUPO
  const getGroupTicketButtons = (ticket) => {
    // ✅ ATENDIENDO: Si el ticket tiene estado "open" y tiene usuario asignado, no mostrar transferir ni finalizar
    const isAttending = ticket.status === "open" && ticket.userId;
    
    if (ticket.contact && ticket.contact.isGroup) {
      // ✅ GRUPOS: Botones específicos según estado
      if (ticket.status === "pending") {
        // ✅ PENDIENTE: Solo botón ACEPTAR
        return ["accept"];
      } else if (ticket.status === "open") {
        // ✅ ABIERTO: Si está siendo atendido, sin botones. Si no, mostrar TRANSFERIR y FINALIZAR
        return isAttending ? [] : ["transfer", "finalize"];
      } else if (ticket.status === "closed") {
        // ✅ CERRADO: Botón REABRIR
        return ["reopen"];
      }
    } else {
      // ✅ CONTACTOS INDIVIDUALES: Flujo normal
      if (ticket.status === "pending") {
        return ["accept", "finalize"];
      } else if (ticket.status === "open") {
        // ✅ ABIERTO: Si está siendo atendido, sin botones. Si no, mostrar TRANSFERIR y FINALIZAR
        return isAttending ? [] : ["transfer", "finalize"];
      } else if (ticket.status === "closed") {
        return ["reopen"];
      }
    }
    return [];
  };
  
  useEffect(() => {
    // ✅ CRÍTICO: Inicializar isMounted al montar el componente
    isMounted.current = true;
    
    if (ticket.userId && ticket.user?.name) {
      if (isMounted.current) {
        setTicketUser(ticket.user.name.toUpperCase());
      }
    } else if (!ticket.userId) {
      if (isMounted.current) setTicketUser(null);
    }

    // ✅ CRÍTICO: Actualizar tags siempre que cambien (comparar por contenido, no solo referencia)
    const currentTags = ticket?.tags || [];
    const tagsChanged = JSON.stringify(currentTags) !== JSON.stringify(tag);
    
    if ((tagsChanged || currentTags.length !== tag.length) && isMounted.current) {
      setTag([...currentTags]); // ✅ Crear nueva referencia del array
    }

    // ✅ Cleanup function: Marcar como desmontado
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ticket.presence,
    ticket.tags,
    ticket.updatedAt,
    ticket.id,
    ticket.userId,
    ticket.user?.name,
    ticket.user?.id,
  ]);

  const handleCloseTicket = () => {
    if (!ticket?.id) return;
    // Misma acción que Resolver (check): cierre estándar sin forzar limpieza de integración/cola en el PUT
    requestCloseTicket(
      { status: "closed", userId: user?.id },
      "Ticket resuelto correctamente"
    );
  };

  useEffect(() => {
    // ✅ CRÍTICO: Inicializar isMounted al montar el componente
    isMounted.current = true;
    
    const renderLastInteractionLabel = () => {
      if (!ticket.lastMessage) return { labelText: '', labelColor: '' };

      // ✅ CORRECCIÓN (31/12/2025): Usar zona horaria configurada de la empresa
      // Las fechas del backend son "naive" (sin zona horaria)
      // Deben interpretarse en la zona horaria de la empresa
      return getTimeAgo(ticket.updatedAt);
    };

    // Função para atualizar o estado do componente
    let timeoutId = null;
    const updateLastInteractionLabel = () => {
      // ✅ CRÍTICO: Verificar si el componente sigue montado antes de actualizar estado
      if (!isMounted.current) {
        if (timeoutId) clearTimeout(timeoutId);
        return;
      }
      
      // ✅ CRÍTICO: Verificar nuevamente antes de actualizar estado
      if (!isMounted.current) return;
      
      const { labelText, labelColor } = renderLastInteractionLabel();
      if (!labelText) {
        if (isMounted.current) {
          setLastInteractionLabel(null);
        }
        return;
      }
      
      // ✅ CRÍTICO: Verificar nuevamente antes de actualizar estado
      if (isMounted.current) {
        setLastInteractionLabel(
          <Typography
            className={classes.lastMessageTime}
            component="span"
            variant="body2"
            style={{
              color: labelColor || undefined,
            }}
          >
            {labelText}
          </Typography>
        );
      }
      
      // ✅ Agendando a próxima atualização após 30 segundos (solo si está montado)
      if (isMounted.current) {
        timeoutId = setTimeout(updateLastInteractionLabel, 30 * 1000);
      }
    };

    // Inicializando a primeira atualização
    updateLastInteractionLabel();

    // ✅ Cleanup function: Limpiar timeout y marcar como desmontado
    return () => {
      isMounted.current = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

  }, [ticket, classes.lastMessageTime, getTimeAgo]); // ✅ Removido isMounted de dependencias (es un ref)

  const handleReopenTicket = async (id) => {
    safeSetLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
      history.push(`/tickets/${ticket.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      safeSetLoading(false);
    }
  };

    const handleAcceptGroupTicket = async (id) => {
      safeSetLoading(true);
      try {
        const { data } = await api.put(`/tickets/${id}`, {
          status: "open",
          userId: user?.id,
        });
        const accepted = {
          ...ticket,
          ...(data && data.id ? data : {}),
          status: "open",
          userId: user?.id,
          user: (data && data.user) || { id: user?.id, name: user?.name, profilePicUrl: user?.profilePicUrl },
          unreadMessages: 0,
        };
        window.dispatchEvent(new CustomEvent("watoolx:ticket-local-update", { detail: accepted }));
        history.push(`/tickets/${ticket.uuid}`);

        if (!ticket.isGroup) {
          const isTelegram =
            ticket.channel === "telegram" || ticket.whatsapp?.type === "telegram";
          if (!isTelegram) {
            getAll()
              .then((list) => {
                const row = list.find((s) => s.key === "sendGreetingAccepted");
                if (row && isSettingTruthy(row.value)) {
                  handleSendMessage(ticket.id);
                }
              })
              .catch(toastError);
          }
        }
      } catch (err) {
        toastError(err);
      } finally {
        safeSetLoading(false);
      }
    };

    // ✅ FUNCIÓN NO UTILIZADA - REEMPLAZADA POR handleAcceptGroupTicket
    // const handleAcepptTicket = async (id) => {
    //     setLoading(true);
    //     try {
    //         await api.put(`/tickets/${id}`, {
    //             status: "open",
    //             userId: user?.id,
    //         });
    //         
    //         let settingIndex;

    //         try {
    //             const { data } = await api.get("/settings/");
    //             
    //             settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
    //             
    //         } catch (err) {
    //             toastError(err);
                   
    //         }
            
    //         if (settingIndex[0].value === "enabled" && !ticket.isGroup) {
    //             handleSendMessage(ticket.id);
                
    //         }

    //     } catch (err) {
    //         setLoading(false);
            
    //         toastError(err);
    //     }
    //     if (isMounted.current) {
    //         setLoading(false);
    //     }

    //     // handleChangeTab(null, "tickets");
    //     // handleChangeTab(null, "open");
    //     history.push(`/tickets/${ticket.uuid}`);
    // };
	
	    const handleSendMessage = async (id) => {
        
        const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `${i18n.t("tickets.automaticMessages.title")}:\n${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
            
        }
    };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
  }

  const handleCloseTransferTicketModal = () => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  };

  // ✅ FUNCIÓN PARA RENDERIZAR BOTONES DE ACCIÓN SEGÚN EL ESTADO DEL GRUPO
  const renderActionButtons = () => {
    const buttons = getGroupTicketButtons(ticket);
    const isAttending = ticket.status === "open" && ticket.userId;
    const isPending = ticket.status === "pending";

    const flagBeforeWhatsApp =
      countryFlagInfo && ticket?.whatsapp ? (
        <Tooltip title={countryFlagInfo.countryName} placement="left" arrow>
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              fontSize: 18,
              lineHeight: 1,
              cursor: "default",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 22,
            }}
            role="img"
            aria-label={countryFlagInfo.countryName}
          >
            {countryFlagInfo.flag}
          </span>
        </Tooltip>
      ) : null;

    // En móvil: solo kebab (atendiendo) o aceptar (espera). El resto vive en el chat.
    if (isMobile) {
      if (isAttending) {
        return (
          <span className={classes.secondaryContentSecond} style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 0 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e);
              }}
              className={classes.mobileActionButton}
              style={{ color: "#666", borderColor: "#ddd" }}
            >
              <MoreVertIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        );
      }
      if (isPending && buttons.includes("accept")) {
        return (
          <span className={classes.secondaryContentSecond} style={{ display: "flex", alignItems: "center", marginBottom: 0 }}>
            <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
              <span>
                <IconButton
                  className={`${classes.mobileActionButton} ${classes.acceptButton}`}
                  size="small"
                  disabled={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcceptGroupTicket(ticket.id);
                  }}
                >
                  <CheckIcon style={{ fontSize: isMobile ? "16px" : "20px" }} />
                </IconButton>
              </span>
            </Tooltip>
          </span>
        );
      }
      return null;
    }

    // ✅ Si está siendo atendido, mostrar los 3 iconos (WhatsApp, ojo, menú)
    if (isAttending) {
    return (
        <span className={classes.secondaryContentSecond} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "16px" }}>
          {flagBeforeWhatsApp}
          {/* ✅ ICONO DE WHATSAPP */}
          {ticket?.whatsapp && (
            <Tooltip title={`${ticket.whatsapp.name || (isTelegramTicket ? "Telegram" : "WhatsApp")} - ${isWhatsAppConnected ? "Conectado" : "Desconectado"}`}>
              <span
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <ChannelIcon
                  style={{
                    fontSize: 18,
                    color: isWhatsAppConnected ? channelColor : "#9ca3af",
                  }}
                />
              </span>
            </Tooltip>
          )}
          {/* ✅ ICONO DEL OJO */}
          {profile === "admin" && (
            <Tooltip title="Espiar Conversa">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenTicketMessageDialog(true);
                }}
                className={classes.actionIconButton}
                style={{ 
                  color: blue[600],
                  borderColor: blue[200],
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* ✅ MENÚ DESPLEGABLE */}
          <Tooltip title="Más opciones">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e);
              }}
              className={classes.actionIconButton}
              style={{ 
                color: "#666",
                borderColor: "#ddd",
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </span>
      );
    }
    
    // ✅ Botones normales para otros estados
    return (
      <span 
        className={classes.secondaryContentSecond}
        style={isPending ? { marginBottom: "16px" } : {}}
      >
        {buttons.includes("accept") && (
          <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.acceptButton}`}
                size="small"
                disabled={loading}
                onClick={e => handleAcceptGroupTicket(ticket.id)}
              >
                <CheckIcon style={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {buttons.includes("transfer") && (
          <Tooltip title={i18n.t("ticketsList.buttons.transfer")}>
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.transferButton}`}
                size="small"
                disabled={loading}
                onClick={e => handleOpenTransferModal()}
              >
                <PeopleIcon style={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {buttons.includes("finalize") && (
          <Tooltip title={i18n.t("ticketsList.buttons.closed")}>
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.closeButton}`}
                size="small"
                disabled={loading}
                onClick={e => handleCloseTicket()}
              >
                <CloseIcon style={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {buttons.includes("reopen") && (
          <Chip
            size="small"
            label="Resuelto"
            style={{
              height: 22,
              fontSize: 11,
              fontWeight: 700,
              backgroundColor: "#4F46F5",
              color: "#fff",
              marginRight: 4,
            }}
          />
        )}
        {buttons.includes("reopen") && (
          <Tooltip title={i18n.t("ticketsList.buttons.reopen")}>
            <span>
              <IconButton
                className={`${classes.actionIconButton} ${classes.reopenButton}`}
                size="small"
                disabled={loading}
                onClick={e => handleReopenTicket(ticket.id)}
              >
                <ReplayIcon style={{ fontSize: "16px" }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {/* ✅ ICONO DE WHATSAPP PARA TICKETS PENDING - Muestra conexión activa */}
        {ticket.status === "pending" && ticket?.whatsapp && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {flagBeforeWhatsApp}
            <Tooltip title={`${ticket.whatsapp.name || (isTelegramTicket ? "Telegram" : "WhatsApp")} - ${isWhatsAppConnected ? "Conectado" : "Desconectado"} | Click para ver conversación`}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenTicketMessageDialog(true);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ cursor: "pointer", display: "inline-flex" }}
              >
                <ChannelIcon
                  style={{
                    fontSize: 18,
                    color: isWhatsAppConnected ? channelColor : "#9ca3af",
                  }}
                />
              </span>
            </Tooltip>
          </span>
        )}
        {/* ✅ ICONO DEL OJO - Solo para tickets que NO están en PENDING */}
        {ticket.status !== "pending" && (
          <Tooltip title="Ver conversación">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpenTicketMessageDialog(true);
              }}
              className={classes.actionIconButton}
              style={{ 
                color: blue[600],
                borderColor: blue[200],
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </span>
    );
  };

  // ✅ Validación: si no hay ticket, no renderizar nada (después de todos los hooks)
  if (!ticket || !ticket.id) {
    return null;
  }

  return (
    <React.Fragment key={ticket.id}>

    <TransferTicketModalCustom
    modalOpen={transferTicketModalOpen}
    onClose={handleCloseTransferTicketModal}
    ticketid={ticket.id}
  />

      <TicketMessagesDialog
        open={openTicketMessageDialog}

        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      ></TicketMessagesDialog>
      <ListItem dense button
        onClick={(e) => {
          // ✅ CORRECCIÓN 2025-12-23: Si el ticket está en PENDING, abrir en modo lectura
          if (ticket.status === "pending") {
            e.stopPropagation();
            setOpenTicketMessageDialog(true);
            return;
          }
          handleSelectTicket(ticket);
        }}
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.unreadTicket]: ticket.unreadMessages > 0,
        })}
      >
        <Tooltip arrow placement="right" title={ticket.queue?.name?.toUpperCase() || ""} >
          <span style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }} className={classes.ticketQueueColor}></span>
        </Tooltip>
        <ListItemAvatar>
          <Box display="flex" flexDirection="column" alignItems="center" style={{ marginRight: 8, position: "relative" }}> {/* ✅ REDUCIDO: De 12px a 8px, agregado position relative */}
            <Box className={classes.avatarContainer} style={{ position: "relative" }}>
          {ticket.status !== "pending" ?
            <Avatar
              key={`avatar-${ticket.id}-${ticket?.contact?.id}-${ticket?.contact?.profilePicUrl || 'no-pic'}`}
                  className={`${classes.avatarActive}${!(ticket?.contact?.profilePicUrl && ticket.contact.profilePicUrl !== "" && !ticket.contact.profilePicUrl.includes("nopicture.png")) ? " avatar-no-photo" : ""}`}
              imgProps={contactPortraitImgProps}
              style={{
                backgroundColor: generateColor(ticket?.contact?.number),
              }}
              src={(ticket?.contact?.profilePicUrl && 
                     ticket.contact.profilePicUrl !== "" && 
                     !ticket.contact.profilePicUrl.includes("nopicture.png")) 
                     ? getProfilePicUrl(ticket.contact.profilePicUrl) 
                     : null}
              onError={(e) => {
                // ✅ FIX 05/12/2025: Si la imagen falla al cargar, ocultar para mostrar iniciales
                if (e.target) {
                  e.target.style.display = 'none';
                }
              }}>
              {getInitials(ticket?.contact?.name || "")}
              </Avatar>
            :
            <Avatar
              key={`avatar-${ticket.id}-${ticket?.contact?.id}-${ticket?.contact?.profilePicUrl || 'no-pic'}`}
                  className={`${classes.avatarPending}${!(ticket?.contact?.profilePicUrl && ticket.contact.profilePicUrl !== "" && !ticket.contact.profilePicUrl.includes("nopicture.png")) ? " avatar-no-photo" : ""}`}
              imgProps={contactPortraitImgProps}
              style={{
                backgroundColor: generateColor(ticket?.contact?.number),
              }}
              src={(ticket?.contact?.profilePicUrl && 
                     ticket.contact.profilePicUrl !== "" && 
                     !ticket.contact.profilePicUrl.includes("nopicture.png")) 
                     ? getProfilePicUrl(ticket.contact.profilePicUrl) 
                     : null}
              onError={(e) => {
                // ✅ FIX 05/12/2025: Si la imagen falla al cargar, ocultar para mostrar iniciales
                if (e.target) {
                  e.target.style.display = 'none';
                }
              }}>
              {getInitials(ticket?.contact?.name || "")}
              </Avatar>
          }
              {/* ✅ AVATAR DEL AGENTE SUPERPUESTO */}
              {(ticket.user || ticket.userId) && (
                <Tooltip title={(ticket.user?.name || ticketUser || "")} arrow>
                  <Box className={classes.agentAvatarBadge}>
                    <Avatar
                      className={classes.agentAvatar}
                      imgProps={agentPortraitImgProps}
                      src={
                        agentPortraitDeferred
                          ? null
                          : ticket.user?.profilePicUrl &&
                            !ticket.user.profilePicUrl.includes("nopicture.png")
                          ? getProfilePicUrl(ticket.user.profilePicUrl)
                          : null
                      }
                      style={{
                        backgroundColor: generateColor(ticket.user?.name || ticketUser || ticket.userId?.toString() || ""),
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                      onError={(e) => {
                        if (e.target) {
                          e.target.style.display = 'none';
                        }
                      }}
                    >
                      {getInitials(ticket.user?.name || ticketUser || "")}
                    </Avatar>
                  </Box>
                </Tooltip>
              )}
            </Box>
            {/* M3: bajo avatar — con asignado: nombre + #ticket; sin asignado: solo #ticket (coherente con TicketInfo `#{ticket.id}`) */}
            <Box className={classes.avatarMetaBelow}>
              {(ticket.userId || ticket.user) &&
                (ticket.user?.name || ticketUser) && (
                  <Typography
                    component="span"
                    variant="caption"
                    className={classes.avatarMetaAssignee}
                    title={ticket.user?.name || ticketUser || ""}
                  >
                    {ticket.user?.name || ticketUser || ""}
                  </Typography>
                )}
              <Typography
                component="span"
                variant="caption"
                className={classes.avatarMetaTicketId}
              >
                #{ticket.id}
              </Typography>
            </Box>
          </Box>
        </ListItemAvatar>
        <ListItemText
          disableTypography

          primary={
            <span className={classes.contactNameWrapper}>
            <Typography
            noWrap
            component='span'
            variant='body2'
            color='textPrimary'
            style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            <strong>{ticket?.contact?.name || 'Contacto sin nombre'}</strong>
              </Typography>
        </span>

          }
          secondary={
            <span className={classes.contactNameWrapper} style={isMobile ? { justifyContent: "flex-start", gap: 6 } : undefined}>
              {isMobile && Number(ticket.unreadMessages) > 0 && (
                <span className={`${classes.unreadCountPill} ${classes.unreadCountPillMobile}`}>
                  {Number(ticket.unreadMessages) > 99 ? "99+" : Number(ticket.unreadMessages)}
                </span>
              )}
              <Typography
                className={classes.contactLastMessage}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
              >
                {["composing", "recording"].includes(ticket?.presence) ? (
                  <span className={classes.presence}>
                    {presenceMessage[ticket.presence]}
                  </span>
                ) : (
                  <>
                    {(() => {
                      const lastMessageText = ticket.lastMessage == null ? '' : (typeof ticket.lastMessage === 'string' ? ticket.lastMessage : (ticket.lastMessage.body ?? ''));
                      // FIX: Para media sin caption (imagen, video, audio, documento), body puede estar vacío.
                      // 1) Usar mediaType de lastMessageWithReactions para etiqueta legible.
                      // 2) Si lastMessage es filename (ej: 1773543495802.jpeg), mostrar etiqueta según extensión.
                      const lastMsg = ticket.lastMessageWithReactions?.[0];
                      const mediaTypeLabels = { image: '[Imagen]', video: '[Video]', audio: '[Audio]', application: '[Documento]', sticker: '[Sticker]' };
                      let fallbackLabel = lastMsg?.mediaType ? (mediaTypeLabels[lastMsg.mediaType] || `[${lastMsg.mediaType}]`) : null;
                      if (!fallbackLabel && typeof lastMessageText === 'string' && lastMessageText) {
                        const ext = (lastMessageText.split('.').pop() || '').toLowerCase();
                        if (['jpg','jpeg','png','gif','webp'].includes(ext)) fallbackLabel = '[Imagen]';
                        else if (['mp4','webm','mov','avi'].includes(ext)) fallbackLabel = '[Video]';
                        else if (['mp3','ogg','wav','m4a'].includes(ext)) fallbackLabel = '[Audio]';
                        else if (['pdf','doc','docx','xls','xlsx'].includes(ext)) fallbackLabel = '[Documento]';
                      }
                      // Si lastMessageText es solo un filename (ej: 1773543495802.jpeg), usar etiqueta legible
                      const isFilenameOnly = lastMessageText && /^[\w.-]+\.(jpg|jpeg|png|gif|webp|mp4|mp3|pdf|doc|docx)$/i.test(lastMessageText);
                      const displayText = lastMessageText && lastMessageText.includes('data:image/png;base64') ? null : 
                        (isFilenameOnly && fallbackLabel ? fallbackLabel : (lastMessageText || fallbackLabel || "Sin mensaje"));
                      return lastMessageText && lastMessageText.includes('data:image/png;base64') ? 
                        <MarkdownWrapper inline> Localização</MarkdownWrapper> 
                        : 
                        <MarkdownWrapper inline>
                          {displayText && displayText.length > 30 
                            ? `${displayText.substring(0, 30)}...` 
                            : displayText
                          }
                        </MarkdownWrapper>;
                    })()}
                    
                    {/* ✅ MOSTRAR REACCIONES DEL ÚLTIMO MENSAJE EN LA VISTA PREVIA */}
                    {ticket.lastMessageWithReactions && ticket.lastMessageWithReactions.length > 0 && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '14px',
                        display: 'inline-block'
                      }}>
                        {ticket.lastMessageWithReactions[0].reactions && ticket.lastMessageWithReactions[0].reactions.length > 0 && (
                          <span style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '12px',
                            padding: '2px 6px',
                            fontSize: '12px',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                          }}>
                            {ticket.lastMessageWithReactions[0].reactions[ticket.lastMessageWithReactions[0].reactions.length - 1].type}
                          </span>
                        )}
                      </span>
                    )}
                  </>
                )}

                {!isMobile && (
                <span style={{ marginTop: 2 }} className={classes.secondaryContentSecond} >
                  {ticket.queue?.name && <Badge style={{ backgroundColor: ticket.queue?.color || "#7c7c7c" }} className={classes.connectionTag}>{ticket.queue?.name?.toUpperCase()}</Badge>}
                  {/* ✅ INDICADORES DE AGENTE IA */}
                  {ticket.chatbot && (
                    <Tooltip title="🤖 Agente IA - Chatbot activo">
                      <AndroidIcon
                        fontSize="small"
                        style={{ 
                          color: blue[600], 
                          fontSize: '16px',
                          marginLeft: '5px',
                          marginTop: '2px',
                          cursor: 'pointer'
                        }}
                      />
                    </Tooltip>
                  )}
                  
                  					{/* ✅ INDICADOR DE PROMPT IA */}
					{ticket.promptId && (
						<Tooltip title="🧠 IA Inteligente - Prompt configurado">
							<EmojiEmotionsIcon
								fontSize="small"
								style={{ 
									color: green[600], 
									fontSize: '16px',
									marginLeft: '5px',
									marginTop: '2px',
									cursor: 'pointer'
								}}
							/>
						</Tooltip>
					)}
                  
                  					{/* ✅ INDICADOR DE INTEGRACIÓN */}
					{ticket.useIntegration && (
						<Tooltip title="🔗 Integración Externa - N8N/Dialogflow">
							<SettingsIcon
								fontSize="small"
								style={{ 
									color: orange[600], 
									fontSize: '16px',
									marginLeft: '5px',
									marginTop: '2px',
									cursor: 'pointer'
								}}
							/>
						</Tooltip>
					)}
                  
                  					{/* ✅ INDICADOR DE FLOWBUILDER */}
					{/* ✅ CRÍTICO: Solo mostrar icono de FlowBuilder en pestaña "ai" */}
					{status === "ai" && (ticket.flowStatus != null || ticket.queue?.flowBuilderId) && (
						<Tooltip title="🌊 FlowBuilder - Automatización de flujos activa">
							<AccountTreeIcon
								fontSize="small"
								style={{ 
									color: green[700], 
									fontSize: '16px',
									marginLeft: '5px',
									marginTop: '2px',
									cursor: 'pointer'
								}}
							/>
						</Tooltip>
					)}
                </span>
                )}

                {/*<span style={{ marginTop: 4, fontSize: 5 }} className={classes.secondaryContentSecond} >
                  {ticketUser ? <Chip size="small" icon={<FaceIcon />} label={ticketUser} variant="outlined" /> : <br></br>}
                </span>*/}

                {!isMobile && (
                <span
                  style={{
                    paddingTop: "1px",
                    marginTop: "2px",
                    display: "inline-flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "2px",
                    maxWidth: "100%",
                  }}
                  className={classes.secondaryContentSecond}
                >
                  {Array.isArray(tag) &&
                    tag.length > 0 &&
                    tag.map((t) => (
                      <Tooltip key={t.id != null ? `tag-${t.id}` : `tag-${t.name}-${t.color}`} title={t.name || ""} placement="top" arrow>
                        <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
                          <LocalOfferIcon
                            fontSize="small"
                            style={{
                              color: t.color || "#9e9e9e",
                              fontSize: "16px",
                              marginTop: "2px",
                              cursor: "default",
                              verticalAlign: "middle",
                            }}
                            aria-label={t.name || "tag"}
                          />
                        </span>
                      </Tooltip>
                    ))}
                </span>
                )}

              </Typography>
            </span>
          }

        />
        {/* ✅ TIEMPO RELATIVO DEL TICKET - Reposicionado más cerca de los botones */}
        <ListItemSecondaryAction style={{ 
          marginTop: isMobile ? 0 : (ticket.lastMessage ? '8px' : '0'),
          marginBottom: isMobile ? 0 : '8px',
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end',
          gap: '4px',
          right: isMobile ? '10px' : '4px',
          paddingRight: '0px',
          ...(isMobile ? { top: 4, transform: 'none', gap: '2px' } : {}),
        }}>
          {/* ✅ Badge de tiempo reposicionado dentro de ListItemSecondaryAction */}
          {!isMobile && Number(ticket.unreadMessages) > 0 && (
            <Tooltip title={i18n.t("ticketsList.unreadMessagesHint", { defaultValue: "Mensajes sin leer" })}>
              <span className={classes.unreadCountPill}>
                {Number(ticket.unreadMessages) > 99 ? "99+" : Number(ticket.unreadMessages)}
              </span>
            </Tooltip>
          )}
          {ticket.lastMessage && lastInteractionLabel}
          {renderActionButtons()}
        </ListItemSecondaryAction>

      
      </ListItem>

      <Divider variant={isMobile ? "fullWidth" : "inset"} component="li" />
      
      {/* ✅ MENÚ DESPLEGABLE CON OPCIONES - Solo cuando está siendo atendido */}
      {ticket.status === "open" && ticket.userId && (
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleResolve}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Resolver" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (ticket?.uuid && typeof openPopup === "function") openPopup(ticket);
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={i18n.t("ticketOptionsMenu.openInNewWindow")} />
          </MenuItem>
          <MenuItem onClick={handleResolveWithoutFarewell}>
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Resolver sin despedida" />
          </MenuItem>
          <MenuItem onClick={handleReturn}>
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Devolver" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleMenuClose();
            handleOpenTransferModal();
          }}>
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={i18n.t("ticketsList.buttons.transfer")} />
          </MenuItem>
          <MenuItem onClick={handleSchedule}>
            <ListItemIcon>
              <ScheduleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Posponer" />
          </MenuItem>
          <MenuItem onClick={handleMarkAsUnread}>
            <ListItemIcon>
              <ChatIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Marcar como no leído" />
          </MenuItem>
          <MenuItem onClick={handleKeepWithMe}>
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mantener conmigo" />
          </MenuItem>
          {/* ✅ Solo mostrar opción "Eliminar" para administradores */}
          {user?.profile === 'admin' && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <CloseIcon fontSize="small" style={{ color: red[500] }} />
            </ListItemIcon>
            <ListItemText primary="Eliminar" primaryTypographyProps={{ style: { color: red[500] } }} />
          </MenuItem>
          )}
        </Menu>
      )}
      
      {/* ✅ MODALES */}
      <AppointmentModal
        open={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
        }}
        appointment={null}
        ticketId={ticket.id}
        initialWhatsappId={ticket.whatsappId}
        initialContact={ticket.contact}
        onSaved={() => {}}
      />
      <ConfirmationModal
        title={`¿Eliminar ticket #${ticket.id} de ${ticket.contact?.name || "contacto desconocido"}?`}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmDelete}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>
      <ConfirmationModal
        title={i18n.t("ticketsList.buttons.confirmCloseWithTagsTitle")}
        open={closeTicketConfirmOpen}
        onClose={() => {
          setCloseTicketConfirmOpen(false);
          pendingClosePayloadRef.current = null;
        }}
        onConfirm={handleConfirmCloseTicketModal}
      >
        {getCloseConfirmModalBody()}
      </ConfirmationModal>
    </React.Fragment>
  );
};

// ✅ MEMOIZACIÓN: Solo re-renderizar si cambian propiedades relevantes
const MemoizedTicketListItemCustom = React.memo(TicketListItemCustom, (prevProps, nextProps) => {
  const tagsSig = (tik) =>
    JSON.stringify((tik?.tags || []).map((x) => [x?.id, x?.name, x?.color]));
  return (
    prevProps.ticket.id === nextProps.ticket.id &&
    prevProps.ticket.status === nextProps.ticket.status &&
    prevProps.ticket.updatedAt === nextProps.ticket.updatedAt &&
    prevProps.ticket.unreadMessages === nextProps.ticket.unreadMessages &&
    prevProps.ticket.userId === nextProps.ticket.userId &&
    prevProps.ticket.user?.name === nextProps.ticket.user?.name &&
    prevProps.ticket.presence === nextProps.ticket.presence &&
    prevProps.ticket.lastMessage?.id === nextProps.ticket.lastMessage?.id &&
    tagsSig(prevProps.ticket) === tagsSig(nextProps.ticket)
  );
});

MemoizedTicketListItemCustom.displayName = 'TicketListItemCustom';

export default MemoizedTicketListItemCustom;
